import { useNavigate } from "react-router-dom";
import Navbar from "@/components/navbar";
import LeadForm from "@/components/forms/LeadForm";
import BenefitsSection from "@/components/benefits-section";
import Footer from "@/components/footer";
import { FORM_CONFIGS } from "@/config/forms";
import { scrollToJoinSection } from "@/lib/scroll-to-join";

const FIXED_DATE_CUTOFF = new Date("2026-07-12T23:59:59.999Z");

function formatShortDate(d: Date) {
  return `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })}`;
}

function getSessionDateLabel(): string {
  const now = new Date();
  if (now <= FIXED_DATE_CUTOFF) {
    return "Date: 11 July & 12 July 2026 (IST/EST)";
  }
  const temp = new Date(now);
  const day = temp.getDay();
  const nextSaturday = new Date(temp);
  if (day === 0) {
    nextSaturday.setDate(temp.getDate() - 1);
  } else {
    const daysUntilSaturday = (6 - day + 7) % 7 || 7;
    nextSaturday.setDate(temp.getDate() + daysUntilSaturday);
  }
  const nextSunday = new Date(nextSaturday);
  nextSunday.setDate(nextSaturday.getDate() + 1);
  return `Date: ${formatShortDate(nextSaturday)} & ${formatShortDate(nextSunday)} ${nextSaturday.getFullYear()} (IST/EST)`;
}

function MastermindHero() {
  const navigate = useNavigate();

  function goToJoinForm() {
    if (scrollToJoinSection()) return;
    void navigate("/mastermind#join");
  }

  return (
    <section className="flex flex-col items-center text-center gap-5">
      <button
        type="button"
        onClick={goToJoinForm}
        className="inline-block bg-white/5 border border-white/40 text-white/80 text-sm font-medium rounded-full px-4 py-1.5 animate-[fade-in_0.5s_ease-out] cursor-pointer hover:bg-white/10 transition-colors"
      >
        Free &middot; 16-Hour AI Training
      </button>

      <h1 className="font-fira-condensed text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight animate-[fade-in_0.5s_ease-out_0.1s_both]">
        Master Generative AI in <br />
        16 Hours
      </h1>

      <h2 className="text-xl sm:text-2xl font-medium text-white/90 max-w-lg animate-[fade-in_0.5s_ease-out_0.2s_both]">
        Learn the skills that top AI professionals use to save time, work
        smarter, and stay ahead.
      </h2>

      <p className="text-secondary text-base sm:text-lg max-w-sm leading-relaxed animate-[fade-in_0.5s_ease-out_0.3s_both]">
        {getSessionDateLabel()}
      </p>

      <button
        type="button"
        onClick={goToJoinForm}
        className="mt-2 animate-[fade-in_0.5s_ease-out_0.5s_both] text-secondary/90 hover:text-white/60 transition-colors cursor-pointer rounded-full p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        aria-label="Scroll to signup form"
      >
        <svg
          className="w-5 h-5 animate-bounce"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </section>
  );
}

export default function MastermindPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-black font-fira max-w-2xl mx-auto px-4 py-8 sm:py-16 flex flex-col gap-16">
        <MastermindHero />
        <section id="join" className="scroll-mt-28">
          <LeadForm
            config={FORM_CONFIGS.mastermind}
            submitLabel="Reserve My Free Seat →"
          />
        </section>
        <BenefitsSection />
        <Footer />
      </main>
    </>
  );
}
